import React, { useCallback } from "react";
import { Box, Button, Paper } from "@mui/material";
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { LoadingPlanSteps } from "../../CreateLoadingPlanPage";
import { useLoadingPlan } from "../../../../contexts/LoadingPlanContext";
import { LoadingPlanStatus } from "../../../../types/loadingPlan";

interface StepFooterProps {
  currentStep: LoadingPlanSteps;
  isGenerating?: boolean;
  isGenerated?: boolean;
  isFinishingLayout?: boolean;
  // True while ViewerComponent recalculation is in flight.
  isViewerRecalculating?: boolean;
  onDelete: () => void;
  onSaveDraft: () => void;
  onPrev: () => void;
  onNext: () => void;
}

const StepFooter: React.FC<StepFooterProps> = ({
  currentStep,
  isGenerating = false,
  isGenerated = false,
  isFinishingLayout = false,
  isViewerRecalculating = false,
  onDelete,
  onSaveDraft,
  onPrev,
  onNext,
}) => {
  const { currentLoadingPlan } = useLoadingPlan();

  const isPlanChanged = currentLoadingPlan.status !== LoadingPlanStatus.READY;

  const getNextButtonLabel = useCallback(() => {
    switch (currentStep) {
      case LoadingPlanSteps.STEP1:
        return "NEXT: CONFIGURE TRAILER";
      case LoadingPlanSteps.STEP2:
        return "NEXT: GENERATE PLAN";
      case LoadingPlanSteps.STEP3:
        if (isPlanChanged) {
          return 'GENERATE PLAN';
        }

        return 'REVIEW & PUBLISH';
      case LoadingPlanSteps.STEP4:
        if (isFinishingLayout) {
          return 'FINISHING LAYOUT...';
        }
        if (isViewerRecalculating) {
          return 'RECALCULATING...';
        }
        return currentLoadingPlan.nestingCoverageComplete === false ? 'FINISH LAYOUT' : 'PUBLISH PLAN';
      default:
        return "NEXT";
    }
  }, [currentStep, isPlanChanged, isFinishingLayout, isViewerRecalculating, currentLoadingPlan.nestingCoverageComplete]);

  const getNextButtonColor = (): "inherit" | "primary" | "secondary" | "success" | "error" | "info" | "warning" => {
    return currentStep === LoadingPlanSteps.STEP3 || currentStep === LoadingPlanSteps.STEP4
      ? 'success'
      : 'primary';
  };

  const isPrevButtonDisabled = currentStep === LoadingPlanSteps.STEP1;
  const isNextButtonDisabled =
    (currentStep === LoadingPlanSteps.STEP3 && isGenerating) ||
    (currentStep === LoadingPlanSteps.STEP4 && (isFinishingLayout || isViewerRecalculating));
  const showSaveAsReady = currentStep === LoadingPlanSteps.STEP4;

  return (
    <Paper className="form-card" sx={{ mt: 3 }}>
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" gap={2}>
          <Button variant="outlined" onClick={onDelete}>
            DELETE
          </Button>
          <Button variant="outlined" color="primary" onClick={onSaveDraft}>
            {showSaveAsReady ? "SAVE AS READY" : "SAVE DRAFT"}
          </Button>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={onPrev}
            disabled={isPrevButtonDisabled}
          >
            {currentStep === LoadingPlanSteps.STEP1 ? "PREV" : "PREV"}
          </Button>
          <Button
            variant="contained"
            color={getNextButtonColor()}
            onClick={onNext}
            disabled={isNextButtonDisabled}
            endIcon={currentStep === LoadingPlanSteps.STEP1 || currentStep === LoadingPlanSteps.STEP2 ? <ArrowForward fontSize="small" /> : undefined}
          >
            {getNextButtonLabel()}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export { StepFooter };
